import React, { useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { TextureLoader } from "three";
import * as THREE from "three";
import { Box } from "@chakra-ui/react";

const Pseudo3DImage = () => {
  const meshRef = useRef();
  const bgMeshRef = useRef();
  const mouse = useRef({ x: 0, y: 0 });
  const isHovering = useRef(false);
  const { camera } = useThree();

  // Load textures
  const colorTexture = useLoader(TextureLoader, "/assets/images/portrait.webp");
  const depthTexture = useLoader(TextureLoader, "/assets/images/portrait-depth.webp");

  // Vertex shader
  const vertexShader = `
    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
      vUv = uv;
      vPosition = position;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  // Fragment shader for pseudo 3D effect with better edge handling
  const fragmentShader = `
    uniform sampler2D colorTexture;
    uniform sampler2D depthTexture;
    uniform vec2 mouse;
    uniform float time;

    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
      vec4 depth = texture2D(depthTexture, vUv);

      // Create parallax displacement based on depth
      vec2 mouseOffset = (mouse - 0.5) * 1.5;
      float depthScale = 0.04; // Further reduced for more subtle effect
      vec2 displacedUV = vUv + mouseOffset * depth.r * depthScale;

      // Check if we're within bounds
      if (displacedUV.x >= 0.0 && displacedUV.x <= 1.0 && 
          displacedUV.y >= 0.0 && displacedUV.y <= 1.0) {
        // Sample color normally
        vec4 color = texture2D(colorTexture, displacedUV);
        gl_FragColor = color;
      } else {
        // For out-of-bounds areas, use a blurred/darkened edge color
        vec2 edgeUV = clamp(displacedUV, 0.02, 0.98);
        vec4 edgeColor = texture2D(colorTexture, edgeUV);
        
        // Calculate distance from valid UV space
        vec2 overflow = max(vec2(0.0), abs(displacedUV - 0.5) - 0.5);
        float fadeOut = 1.0 - smoothstep(0.0, 0.15, length(overflow));
        
        // Darken and fade the edge
        gl_FragColor = vec4(edgeColor.rgb * 0.3, edgeColor.a * fadeOut);
      }
    }
  `;

  // Background shader (blurred, static version)
  const bgFragmentShader = `
    uniform sampler2D colorTexture;
    varying vec2 vUv;

    void main() {
      // Simple blur by sampling nearby pixels
      vec4 color = vec4(0.0);
      float blurSize = 0.004;
      
      for(float x = -2.0; x <= 2.0; x++) {
        for(float y = -2.0; y <= 2.0; y++) {
          vec2 offset = vec2(x, y) * blurSize;
          color += texture2D(colorTexture, vUv + offset);
        }
      }
      
      color /= 25.0; // Average of 5x5 samples
      color.rgb *= 0.5; // Darken background
      
      gl_FragColor = color;
    }
  `;

  // Foreground shader material
  const shaderMaterial = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          colorTexture: { value: colorTexture },
          depthTexture: { value: depthTexture },
          mouse: { value: new THREE.Vector2(0.5, 0.5) },
          time: { value: 0 },
        },
        vertexShader,
        fragmentShader,
        transparent: true,
        side: THREE.DoubleSide,
      }),
    [colorTexture, depthTexture, vertexShader, fragmentShader]
  );

  // Background shader material
  const bgShaderMaterial = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          colorTexture: { value: colorTexture },
        },
        vertexShader,
        fragmentShader: bgFragmentShader,
        side: THREE.DoubleSide,
      }),
    [colorTexture, vertexShader, bgFragmentShader]
  );

  // Handle mouse movement
  const handleMouseMove = (event) => {
    mouse.current.x = event.uv.x;
    mouse.current.y = event.uv.y;
    isHovering.current = true;
  };

  useFrame((state) => {
    if (meshRef.current && shaderMaterial.uniforms) {
      if (!isHovering.current) {
        // Animate left and right when not hovering
        const time = state.clock.elapsedTime;
        const animatedX = 0.5 + 0.35 * Math.sin(time * 2); // Bouncing smoother animation
        mouse.current.x = animatedX;
        mouse.current.y = 0.5;
      }
      shaderMaterial.uniforms.mouse.value.set(mouse.current.x, mouse.current.y);
      shaderMaterial.uniforms.time.value = state.clock.elapsedTime;

      // Animate camera position based on hover state
      const targetPosition = isHovering.current
        ? new THREE.Vector3(0, 0, 2.1)
        : new THREE.Vector3(0, 0, 2.2);
      camera.position.lerp(targetPosition, 0.1);
    }
  });

  return (
    <group>
      {/* Background layer - blurred and static */}
      <mesh ref={bgMeshRef} material={bgShaderMaterial} position={[0, 0, -0.01]}>
        <planeGeometry args={[2, 2 * (1058 / 954)]} />
      </mesh>

      {/* Foreground layer - with parallax effect */}
      <mesh
        ref={meshRef}
        material={shaderMaterial}
        onPointerMove={handleMouseMove}
        onPointerLeave={() => {
          isHovering.current = false;
          mouse.current = { x: 0.8, y: 0.8 };
        }}
      >
        <planeGeometry args={[2, 2 * (1058 / 954)]} />
      </mesh>
    </group>
  );
};

const ProfilePicture = () => {
  const containerRef = useRef();
  const [isVisible, setIsVisible] = useState(false);
  const [cameraZ, setCameraZ] = useState(5);

  // Function to calculate camera Z based on screen size
  const updateCameraZ = () => {
    const width = window.innerWidth;
    if (width < 768) {
      setCameraZ(4); // Closer on mobile for better fit
    } else if (width < 1024) {
      setCameraZ(4.5); // Medium on tablet
    } else {
      setCameraZ(5); // Default on desktop
    }
  };

  // Intersection observer to only render Canvas when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Update camera Z on mount and resize
  useEffect(() => {
    updateCameraZ();
    const handleResize = () => updateCameraZ();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      ref={containerRef}
      w={{ base: "350px", sm: "350px", md: "450px", lg: "600px" }}
      h={{ base: "600px", sm: "600px", md: "600px", lg: "800px" }}
      position="relative"
    >
      <Canvas
        frameloop={isVisible ? "always" : "never"}
        camera={{ position: [0, 0, cameraZ], fov: 50 }}
        gl={{
          antialias: false,
          alpha: true,
          powerPreference: "default",
          failIfMajorPerformanceCaveat: false,
        }}
      >
        <Pseudo3DImage />
      </Canvas>
    </Box>
  );
};

export default React.memo(ProfilePicture);
